import { Alert, AlertTitle, Stack } from "@mui/material";
import { ExtractionReviewDialog, ExtractionReviewGrid } from "components/Document/ExtractionReview";
import { useMemo } from "react";

import {
  toExtractionResult,
  toRecordValues,
  WASTE_PROFILE_REVIEW_SCHEMA,
} from "./extractionReview";
import { WasteStreamProfileData } from "./types";
import { useApplyReviewedProfile } from "./useApplyReviewedProfile";
import { useProfileReading } from "./useProfileReading";

export const AutoFillProfileDialog = ({
  open,
  onClose,
  wasteStream,
  documentId,
  documentTitle,
}: {
  open: boolean;
  onClose: () => void;
  /** The stream as it stands, so the grid can show what the sheet does not say. */
  wasteStream: WasteStreamProfileData;
  documentId: string;
  documentTitle: string;
}) => {
  // Reads the document on open when nothing has read it yet — see
  // `useProfileReading`. There is no prompt to read it, so no state here needs to
  // offer one.
  const { reading, loading, status, startError, requestReading } = useProfileReading(documentId);

  const { validate, confirm, applying, blockingIssues } = useApplyReviewedProfile({
    wasteStreamId: wasteStream.id,
    documentId,
    onApplied: onClose,
  });

  const extraction = useMemo(
    () => (reading ? toExtractionResult(reading, wasteStream) : null),
    [reading, wasteStream]
  );

  // What applying this reading would write over. The grid flags the divergence
  // rather than deciding it, so a hand-entered value or a constituent the sheet
  // does not list is the reviewer's call instead of a silent replacement.
  const record = useMemo(() => toRecordValues(wasteStream), [wasteStream]);

  return (
    <ExtractionReviewDialog
      open={open}
      // Closing mid-apply would drop the reviewer's confirmation while the write
      // is still in flight, leaving them unsure whether it landed.
      onClose={() => {
        if (!applying) onClose();
      }}
      // Matches the document-intelligence ingestion flow, which the reviewer may
      // well have arrived from: same activity, same name.
      title="Review & Confirm Details"
      document={wasteStream.profile?.profileDocument}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {blockingIssues.length > 0 && (
          <Alert severity="error">
            <AlertTitle>Fix these before applying</AlertTitle>
            <ul style={{ margin: 0, paddingInlineStart: "1.25rem" }}>
              {blockingIssues.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}

        <ExtractionReviewGrid
          schema={WASTE_PROFILE_REVIEW_SCHEMA}
          extraction={extraction}
          record={record}
          status={status}
          loading={loading}
          extractionError={startError ?? reading?.extractionError}
          onCommit={confirm}
          onCancel={onClose}
          onRetry={requestReading}
          validator={validate}
          committing={applying}
          confirmLabel="Apply to profile"
          description={`Read from ${documentTitle}. Review each value before applying it — everything here is editable.`}
        />

        {!loading && reading == null && (
          <Alert severity="info">This document is no longer available to read.</Alert>
        )}
      </Stack>
    </ExtractionReviewDialog>
  );
};
